export interface LicensePayload {
  id: string;
  iat: number;
  exp: number;
}

export type LicenseCheckResult =
  | { valid: true; payload: LicensePayload }
  | {
      valid: false;
      reason: "none" | "bad-format" | "bad-payload" | "bad-signature" | "expired" | "verify-failed";
      payload?: LicensePayload;
    };

declare global {
  interface Window {
    electronAPI?: {
      apiUrl: string;
      getWorkspaceDir: () => Promise<string>;
      changeWorkspaceDir: () => Promise<string | null>;
      getMcpConfig: () => Promise<{ serverUrl: string; hasToken: boolean }>;
      saveMcpConfig: (serverUrl: string, authToken: string) => Promise<void>;
      openFileDialog: (options: unknown) => Promise<{ canceled: boolean; filePaths: string[] }>;
      onUpdateAvailable: (cb: (info: { version: string; url: string }) => void) => void;
      onFlaskCrashed: (cb: () => void) => void;

      // Desktop viewer: a PDF opened from Explorer / "Open with" / drag-drop,
      // or picked via the native dialog. readFile returns the bytes for a path.
      onOpenPdf: (cb: (payload: { path: string }) => void) => void;
      readFile: (filePath: string) => Promise<{ name: string; data: Uint8Array } | null>;
      pickPdf: () => Promise<{ name: string; path: string; data: Uint8Array } | null>;
      getRecentFiles: () => Promise<Array<{ path: string; name: string }>>;
      addRecentFile: (filePath: string) => Promise<void>;

      // Desktop tools (Milestone B): saving output and picking extra inputs.
      saveFileDialog: (suggestedName: string) => Promise<string | null>;
      writeFile: (filePath: string, data: Uint8Array) => Promise<boolean>;
      pickDirectory: () => Promise<string | null>;
      pickPdfs: () => Promise<Array<{ name: string; data: Uint8Array }>>;

      // Lazy backend lifecycle (Milestone C).
      ensureBackend: () => Promise<{ port: number; apiUrl: string }>;
      getBackendStatus: () => Promise<{ state: string; port: number; apiUrl: string }>;
      stopBackend: () => Promise<void>;
      onBackendStatus: (cb: (info: { state: string; port: number }) => void) => void;

      // Licensing: offline key check, no network, no stored user data.
      getLicenseStatus: () => Promise<LicenseCheckResult>;
      activateLicense: (key: string) => Promise<LicenseCheckResult>;
      deactivateLicense: () => Promise<void>;

      // Help / About.
      getAppVersion: () => Promise<string>;
      openDefaultAppsSettings: () => Promise<void>;

      // Crash reporting (local log, no external service).
      reportCrash: (info: { message: string; stack?: string; context?: unknown }) => Promise<void>;
    };
  }
}

// In desktop mode, Electron's preload injects window.electronAPI.apiUrl with the
// dynamic port. In web mode, fall back to the env var or the default dev port.
const API_URL: string =
  window.electronAPI?.apiUrl ?? process.env.REACT_APP_API_URL ?? 'http://127.0.0.1:5900';

// Build target. The desktop build sets REACT_APP_BUILD_TARGET=desktop so we can
// strip web-marketing chrome and render the document-centric desktop shell
// instead of the web routes — without deleting the web code. IS_DESKTOP also
// returns true at runtime inside Electron even if the flag wasn't set at build.
const BUILD_TARGET: string = process.env.REACT_APP_BUILD_TARGET ?? "web";
const IS_DESKTOP: boolean =
  BUILD_TARGET === "desktop" || !!window.electronAPI;

// Must match the backend's MAX_FILE_SIZE_MB default (backend/src/config.py)
// so the client-side check rejects oversized files before upload instead of
// letting them through to a confusing generic "Upload failed" from the
// server. If you change MAX_FILE_SIZE_MB on the backend, set
// REACT_APP_MAX_FILE_SIZE_MB to match on the frontend deployment.
const MAX_FILE_SIZE_MB = Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 25;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// OCR / AI bank-statement analysis needs GOOGLE_APPLICATION_CREDENTIALS set on
// the backend, which isn't provisioned yet. Hidden by default until that's
// wired up; flip via REACT_APP_FEATURE_OCR=true once credentials are in place.
const FEATURE_OCR_ENABLED: boolean = process.env.REACT_APP_FEATURE_OCR === "true";

export { API_URL, MAX_FILE_SIZE_MB, MAX_FILE_SIZE_BYTES, BUILD_TARGET, IS_DESKTOP, FEATURE_OCR_ENABLED };
